/**
 * REST transport error — thrown by restfulRequester on any non-2xx response.
 *
 * Domain-agnostic: accounts, tickets, and other endpoints all throw this.
 * REST hooks import this for error generics; components should not.
 * Intl mapping and UX fallbacks live per domain
 * (e.g. `intlIdForTicketingError` in `restful/tickets/utils`).
 *
 * OpenAPI documents 4xx only (API-domain). 5xx is typically infrastructure.
 */

// ts-unused-exports:disable-next-line
export class RestApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly method: string;
  readonly path: string;

  constructor(
    message: string,
    status: number,
    body: unknown,
    method: string,
    path: string,
  ) {
    super(message);
    this.name = 'RestApiError';
    this.status = status;
    this.body = body;
    this.method = method;
    this.path = path;
  }

  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isServerError(): boolean {
    return this.status >= 500 && this.status < 600;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isBadRequest(): boolean {
    return this.status === 400;
  }
}

const getRestApiErrorCode = (body: unknown): string | undefined => {
  if (body == null || typeof body !== 'object') return undefined;

  const record = body as Record<string, unknown>;
  if (typeof record.errorCode === 'string') return record.errorCode;
  if (typeof record.errorType === 'string') return record.errorType;
  if (typeof record.code === 'string') return record.code;

  return undefined;
};

export const getErrorCode = (error: RestApiError): string | undefined =>
  getRestApiErrorCode(error.body);
